import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { fabric } from 'fabric';

@Component({
  selector: 'app-edit-info-section',
  templateUrl: './edit-info-section.component.html',
  styleUrls: ['./edit-info-section.component.scss']
})
export class EditInfoSectionComponent implements OnInit {

  @Input() sectionElement!: any;
  @Input() canvas!: any;
  @Input() cancel!: any;
  @Input() levels: any[] = [];
  @Input() availableLevels: { id: number; name: string }[] = [];
  @Input() availableShapes: { id: string; name: string; type?: string }[] = [];
  @Input() colorDefault: string = '#d3d3d3';
  @Input() colorBorde: string = '#d3d3d3';

  sectionNumber: number = 0;
  sectionName: string = '';
  selectedLevel: any = null;

  @Output() updateCanvas = new EventEmitter<any>();
  @Output() copyObject = new EventEmitter<any>();
  @Output() closeTab = new EventEmitter<any>();

  firstTime: boolean = false;
  editForm: boolean = false;
  hasBorder: boolean = true;
  // Grosor del borde de las formas con relleno (polígono/rectángulo/elipse).
  borderWidth: number = 1;
  hasBackground: boolean = true;
  valueAngle: number = 0;
  // true cuando la forma pertenece a la capa preview (plano general): es una zona clicable
  // a la que se le asignan niveles.
  isZone: boolean = false;
  // true cuando la forma es una línea: no tiene relleno, solo trazo (color, grosor y curvatura).
  isLine: boolean = false;
  lineWidth: number = 3;
  curvature: number = 0;
  // true cuando la forma es un polígono: puede redondear sus aristas.
  isPolygon: boolean = false;
  // Radio de redondeo de las aristas del polígono (0 = esquinas rectas).
  cornerRadius: number = 0;
  // true mientras el modo "Curvar aristas" está activo: muestra un tirador en el centro de
  // cada arista que, arrastrado perpendicular, abomba esa arista.
  curveEdgesMode: boolean = false;
  navZoneLevels: number[] = [];
  // Mapa booleano id→checked para el binding [(ngModel)] de cada checkbox.
  selectedLevelsMap: { [id: number]: boolean } = {};
  // Texto del buscador del selector de niveles de la zona.
  levelSearch: string = '';
  navZoneShapes: string[] = [];
  selectedShapesMap: { [id: string]: boolean } = {};
  shapeSearch: string = '';
  // Tamaño de fuente del label flotante de la zona (visible en el editor y en el plano general).
  labelFontSize: number = 40;
  labelAngle: number = 0;
  hideLabel: boolean = false;

  constructor() { }

  ngOnInit(): void {
    this.colorDefault = this.sectionElement.fill;
    this.colorBorde = this.sectionElement.stroke;
    this.hasBorder = this.sectionElement.strokeWidth != 0;
    // Conservar el grosor elegido (mínimo 1) para restaurarlo al reactivar el borde.
    this.borderWidth = this.sectionElement.strokeWidth || 1;
    this.sectionNumber = this.sectionElement.area;
    this.sectionName = this.sectionElement.name;
    this.valueAngle = this.sectionElement.angle || 0;
    this.isZone = !!this.sectionElement.previewLayer;
    // La línea es un fabric.Path (type 'path'); se identifica por el flag isLine.
    this.isLine = this.sectionElement.isLine === true;
    this.lineWidth = this.sectionElement.strokeWidth || 3;
    this.curvature = this.sectionElement.curvature || 0;
    this.isPolygon = this.sectionElement.type === 'polygon';
    this.cornerRadius = this.sectionElement.cornerRadius || 0;
    this.labelFontSize = this.sectionElement.labelFontSize || 40;
    this.labelAngle = this.sectionElement.labelAngle || 0;
    this.hideLabel = !!this.sectionElement.hideLabel;
    this.navZoneLevels = Array.isArray(this.sectionElement.navZoneLevels)
      ? [...this.sectionElement.navZoneLevels]
      : [];
    // Construir el mapa booleano para los checkboxes.
    this.selectedLevelsMap = {};
    this.navZoneLevels.forEach(id => { this.selectedLevelsMap[+id] = true; });
    this.navZoneShapes = Array.isArray(this.sectionElement.navZoneShapes)
      ? [...this.sectionElement.navZoneShapes]
      : [];
    this.selectedShapesMap = {};
    this.navZoneShapes.forEach(id => { this.selectedShapesMap[id] = true; });
    // Normalizar el nivel almacenado: puede ser un entero (id) o un objeto completo (formato heredado)
    const rawLevel = this.sectionElement.level;
    const storedLevelId = (rawLevel != null && typeof rawLevel === 'object') ? rawLevel.id : rawLevel;
    // Buscar por id, luego por id_level (ID externo que guarda el mapa), luego por orderBuy (legacy)
    this.selectedLevel = this.levels?.find((l: any) => +l.id === +storedLevelId)
      || this.levels?.find((l: any) => l.id_level != null && +l.id_level === +storedLevelId)
      || this.levels?.find((l: any) => l.orderBuy != null && +l.orderBuy === +storedLevelId)
      || null;
  }

  editColor() {
    // La línea no tiene relleno (es un path abierto): solo se cambia el trazo.
    if (this.isLine) {
      this.sectionElement.set('stroke', this.colorBorde);
      this.sectionElement.dirty = true;
      this.canvas?.renderAll();
      this.updateCanvas.emit();
      return;
    }
    if(this.hasBackground) {
      this.sectionElement.set('fill', this.colorDefault);
    } else {
      this.sectionElement.set('fill', 'transparent');
    }
    if(this.hasBorder) {
      this.sectionElement.set('stroke', this.colorBorde);
    }
    this.sectionElement.dirty = true;
    this.canvas?.renderAll();
    this.updateCanvas.emit();
  }

  editSection() {
    this.sectionElement.set('area', this.sectionNumber);
    this.updateCanvas.emit();
  }

  editLevel() {
    const levelId = this.selectedLevel ? +this.selectedLevel.id : null;
    const levelColor = this.selectedLevel?.color || null;
    this.sectionElement.set('level', levelId);
    if (levelColor) {
      this.sectionElement.set('fill', levelColor);
      this.sectionElement.set('oldFill', levelColor);
      this.colorDefault = levelColor;
    }
    this.canvas.renderAll();
    this.updateCanvas.emit();
  }

  editNameSection() {
    this.sectionElement.set('name', this.sectionName);
    this.updateCanvas.emit();
  }

  editLabelFontSize() {
    this.sectionElement.set('labelFontSize', this.labelFontSize);
    this.sectionElement.dirty = true;
    this.updateCanvas.emit();
  }

  editLabelAngle() {
    this.sectionElement.set('labelAngle', this.labelAngle);
    this.updateCanvas.emit();
  }

  editHideLabel() {
    this.sectionElement.set('hideLabel', this.hideLabel);
    this.updateCanvas.emit();
  }

  // Niveles que pasan el filtro del buscador (sin acentos, sin distinguir mayúsculas).
  get filteredLevels(): { id: number; name: string }[] {
    const q = this.normalizeText(this.levelSearch);
    if (!q) return this.availableLevels;
    return this.availableLevels.filter(l => this.normalizeText(l.name).includes(q));
  }

  // Nº de niveles seleccionados (sobre el total, no solo los filtrados).
  get selectedCount(): number {
    return Object.keys(this.selectedLevelsMap).filter(id => this.selectedLevelsMap[+id]).length;
  }

  get filteredShapes(): { id: string; name: string; type?: string }[] {
    const q = this.normalizeText(this.shapeSearch);
    if (!q) return this.availableShapes;
    return this.availableShapes.filter(s => this.normalizeText(s.name).includes(q));
  }

  get groupedShapes(): { label: string; shapes: { id: string; name: string; type?: string }[] }[] {
    const TYPE_LABELS: { [type: string]: string } = {
      polygon: 'Polígonos',
      rect: 'Rectángulos',
      ellipse: 'Elipses',
      line: 'Líneas',
      text: 'Textos',
    };
    const ORDER = ['polygon', 'rect', 'ellipse', 'line', 'text'];
    const byType: { [type: string]: { id: string; name: string; type?: string }[] } = {};
    for (const shape of this.filteredShapes) {
      const t = shape.type || 'unknown';
      if (!byType[t]) byType[t] = [];
      byType[t].push(shape);
    }
    const groups = [];
    for (const t of ORDER) {
      if (byType[t]?.length) groups.push({ label: TYPE_LABELS[t] || t, shapes: byType[t] });
    }
    for (const t of Object.keys(byType)) {
      if (!ORDER.includes(t) && byType[t].length) groups.push({ label: TYPE_LABELS[t] || t, shapes: byType[t] });
    }
    return groups;
  }

  get selectedShapesCount(): number {
    return Object.keys(this.selectedShapesMap).filter(id => this.selectedShapesMap[id]).length;
  }

  collapsedGroups: { [label: string]: boolean } = {};

  toggleGroupCollapsed(label: string) {
    this.collapsedGroups = { ...this.collapsedGroups, [label]: !this.collapsedGroups[label] };
  }

  trackByGroupLabel(_: number, group: { label: string }): string { return group.label; }
  trackByShapeId(_: number, shape: { id: string }): string { return shape.id; }

  private normalizeText(text: any): string {
    return (text ?? '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  }

  // Activa/desactiva un nivel en esta zona de navegación.
  // Usa el mapa booleano para evitar problemas de sincronía con mat-checkbox.
  toggleLevel(checked: boolean, levelId: number) {
    this.selectedLevelsMap = { ...this.selectedLevelsMap, [+levelId]: !!checked };
    this.syncNavZoneLevels();
  }

  // Marca/desmarca de golpe los niveles actualmente visibles según el filtro.
  setLevelsSelected(selected: boolean) {
    const map = { ...this.selectedLevelsMap };
    this.filteredLevels.forEach(l => { map[+l.id] = selected; });
    this.selectedLevelsMap = map;
    this.syncNavZoneLevels();
  }

  // Vuelca el mapa booleano a navZoneLevels y persiste en el objeto fabric.
  private syncNavZoneLevels() {
    this.navZoneLevels = Object.keys(this.selectedLevelsMap)
      .filter(id => this.selectedLevelsMap[+id])
      .map(id => +id);
    this.sectionElement.set('navZoneLevels', [...this.navZoneLevels]);
    this.sectionElement.dirty = true;
    this.canvas?.renderAll();
    this.updateCanvas.emit();
  }

  toggleShape(checked: boolean, shapeId: string) {
    this.selectedShapesMap = { ...this.selectedShapesMap, [shapeId]: !!checked };
    this.syncNavZoneShapes();
  }

  setShapesSelected(selected: boolean) {
    const map = { ...this.selectedShapesMap };
    this.filteredShapes.forEach(s => { map[s.id] = selected; });
    this.selectedShapesMap = map;
    this.syncNavZoneShapes();
  }

  private syncNavZoneShapes() {
    this.navZoneShapes = Object.keys(this.selectedShapesMap).filter(id => this.selectedShapesMap[id]);
    this.sectionElement.set('navZoneShapes', [...this.navZoneShapes]);
    this.sectionElement.dirty = true;
    this.canvas?.renderAll();
    this.updateCanvas.emit();
  }

  activeEditing() {
  }

  addVertex() {
    if (!this.sectionElement || !this.sectionElement.points) return;
    const points = this.sectionElement.points;
    // Insertar un nuevo punto en la mitad del último segmento
    const lastIndex = points.length - 1;
    const p1 = points[lastIndex];
    const p2 = points[0];
    const newPoint = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2
    };
    points.push(newPoint);
    this.sectionElement.points = points;
    // Mantener edgeCurvatures alineado con el nuevo nº de aristas.
    this.ensureEdgeCurvatures();
    // Refrescar controles si estamos en modo edición
    if (this.editForm) {
      this.editFormSection();
    }
    this.updateCanvas.emit();
  }

  removeLastVertex() {
    if (!this.sectionElement || !this.sectionElement.points) return;
    if (this.sectionElement.points.length <= 3) return; // mínimo triángulo
    this.sectionElement.points.pop();
    // Mantener edgeCurvatures alineado con el nuevo nº de aristas.
    this.ensureEdgeCurvatures();
    if (this.editForm) {
      this.editFormSection();
    }
    this.updateCanvas.emit();
  }

  editFormSection() {
    if (!this.editForm) {
      this.cancelEditForm();
    } else {
      // Los dos modos de edición sobre controles son excluyentes.
      this.curveEdgesMode = false;
      this.buildVertexControls();
      this.updateCanvas.emit();
    }
  }

  // Activa/desactiva el modo "Curvar aristas": tiradores en el centro de cada arista.
  editCurveEdges() {
    if (!this.curveEdgesMode) {
      this.cancelEditForm();
    } else {
      this.editForm = false;
      this.buildEdgeCurvatureControls();
      this.updateCanvas.emit();
    }
  }

  // Garantiza que edgeCurvatures sea un array de longitud == nº de aristas (== nº de vértices).
  // Conserva los valores existentes y rellena con 0 los que falten.
  private ensureEdgeCurvatures(): number[] {
    const n = this.sectionElement?.points?.length || 0;
    let arr: number[] = Array.isArray(this.sectionElement.edgeCurvatures)
      ? this.sectionElement.edgeCurvatures.slice(0, n)
      : [];
    while (arr.length < n) { arr.push(0); }
    this.sectionElement.set('edgeCurvatures', arr);
    return arr;
  }

  // Construye un tirador de curvatura por arista, colocado en el ápice actual de la curva
  // (punto medio de la arista desplazado perpendicularmente edgeCurvatures[i]/2).
  buildEdgeCurvatureControls() {
    const el = this.sectionElement;
    if (!el || !Array.isArray(el.points) || el.points.length < 2) return;
    const n = el.points.length;
    this.ensureEdgeCurvatures();
    el.set('edit', true);
    el.set('objectCaching', false);
    el.set('hasBorders', false);
    el._editComponentRef = this;
    const controls: any = {};
    for (let i = 0; i < n; i++) {
      controls['c' + i] = new fabric.Control({
        positionHandler: edgeCurvaturePositionHandler,
        actionHandler: edgeCurvatureActionHandler,
        mouseUpHandler: edgeCurvatureMouseUpHandler,
        actionName: 'curveEdge',
        render: renderCurveEdgeControl,
        edgeIndex: i,
        sizeX: 16,
        sizeY: 16
      } as any);
    }
    el.controls = controls;
    this.canvas?.renderAll();
  }

  // Construye (o reconstruye) los controles del polígono en modo edición:
  // un tirador por vértice (pN) y un tirador "+" semitransparente en cada arista (mN)
  // que, al arrastrarlo, inserta un vértice nuevo justo en ese punto.
  buildVertexControls() {
    const el = this.sectionElement;
    if (!el || !Array.isArray(el.points)) return;
    const n = el.points.length;
    const lastControl = n - 1;
    el.set("edit", true);
    el.set("objectCaching", false);
    el.set("cornerStyle", 'circle');
    el.set("cornerColor", 'blue');
    el.set("transparentCorners", false);
    el.set("hasBorders", false);
    // Referencia al componente para poder reconstruir los controles al soltar el arrastre.
    el._editComponentRef = this;
    el._activeMidpointInsert = null;
    const controls: any = {};
    for (let i = 0; i < n; i++) {
      controls['p' + i] = new fabric.Control({
        positionHandler: polygonPositionHandler,
        actionHandler: anchorWrapper(i > 0 ? i - 1 : lastControl, actionHandler),
        actionName: 'modifyPolygon',
        pointIndex: i
      } as any);
      // Tirador "+" en el punto medio de la arista i -> (i+1)%n.
      controls['m' + i] = new fabric.Control({
        positionHandler: midpointPositionHandler,
        actionHandler: anchorWrapper(i, addVertexActionHandler),
        mouseUpHandler: midpointMouseUpHandler,
        actionName: 'addVertex',
        render: renderAddVertexControl,
        edgeIndex: i,
        sizeX: 14,
        sizeY: 14
      } as any);
    }
    el.controls = controls;
    this.canvas?.renderAll();
  }


  editBackground() {
    var colorBackground = this.hasBackground ? this.colorDefault : 'transparent';
    this.sectionElement.set('fill', colorBackground);
    this.updateCanvas.emit();
  }

  editLineWidth() {
    const width = +this.lineWidth;
    if (!width || width < 1) return;
    this.sectionElement.set('strokeWidth', width);
    this.sectionElement.dirty = true;
    this.canvas?.renderAll();
    this.updateCanvas.emit();
  }

  // Aplica la curvatura a la línea reconstruyendo su path de Bézier en el sitio,
  // conservando posición (left/top) para que no salte al curvarse.
  editCurvature() {
    const points = this.sectionElement.linePoints || [0, 0, 300, 0];
    const curvature = +this.curvature || 0;
    const [x1, y1, x2, y2] = points;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const px = -dy / len;
    const py = dx / len;
    const qx = mx + px * curvature;
    const qy = my + py * curvature;
    const pathStr = `M ${x1} ${y1} Q ${qx} ${qy} ${x2} ${y2}`;
    this.sectionElement.set('curvature', curvature);
    // _setPath recalcula dimensiones/pathOffset manteniendo left/top indicados.
    if (typeof this.sectionElement._setPath === 'function') {
      this.sectionElement._setPath(pathStr, { left: this.sectionElement.left, top: this.sectionElement.top });
    }
    this.sectionElement.set('dirty', true);
    this.sectionElement.setCoords();
    this.canvas?.renderAll();
    this.updateCanvas.emit();
  }

  // Aplica el radio de redondeo a las aristas del polígono. Solo cambia el trazado
  // (el render personalizado de fabric.Polygon lee `cornerRadius`), no los vértices.
  editCornerRadius() {
    const radius = +this.cornerRadius || 0;
    this.cornerRadius = radius;
    this.sectionElement.set('cornerRadius', radius);
    this.sectionElement.dirty = true;
    this.canvas?.renderAll();
    this.updateCanvas.emit();
  }

  editBorder() {
    if (!this.hasBorder) {
      this.sectionElement.set("strokeWidth", 0);
    } else {
      // Restaurar el último grosor elegido (mínimo 1).
      this.sectionElement.set("strokeWidth", this.borderWidth && this.borderWidth >= 1 ? this.borderWidth : 1);
    }
    this.canvas?.renderAll();
    this.updateCanvas.emit();
  }

  editBorderWidth() {
    const width = +this.borderWidth;
    if (!width || width < 1) return;
    this.borderWidth = width;
    this.sectionElement.set('strokeWidth', width);
    this.sectionElement.dirty = true;
    this.canvas?.renderAll();
    this.updateCanvas.emit();
  }

  copyForm() {
    this.copyObject.emit();
  }

  cancelEditForm() {
    this.editForm = false;
    this.curveEdgesMode = false;
    this.sectionElement.set("edit", false);
    this.sectionElement.set("cornerColor", "rgba(0,0,255,0.5)");
    // La comba de las aristas curvadas sobresale del bounding-box de los vértices, y el
    // canvas de caché de fabric se dimensiona a ese bounding-box: al cachear se recortaría
    // la curva y la arista se vería recta. Mantener objectCaching desactivado cuando hay
    // curvatura (igual que al crear la forma).
    const hasEdgeCurve = Array.isArray(this.sectionElement.edgeCurvatures)
      && this.sectionElement.edgeCurvatures.some((c: number) => c);
    this.sectionElement.set("objectCaching", !hasEdgeCurve);
    this.sectionElement.set("dirty", true);
    this.sectionElement.set("cornerStyle", 'rect');
    this.sectionElement.set("transparentCorners", true);
    this.sectionElement.set("controls", fabric.Object.prototype.controls);
    this.sectionElement.set("hasBorders", true);
    this.sectionElement._activeMidpointInsert = null;
    this.updateCanvas.emit();
  }

  cancelEdit(exit?: boolean) {
    this.cancelEditForm();
    this.closeTab.emit(true);
  }

  editRotation(value: any) {
    this.sectionElement.set('angle', parseInt(value, 10)).setCoords();
    this.updateCanvas.emit();
  }

  applyChanges() {
    this.editColor();
    this.cancelEditForm();
    this.editForm = false;
    this.updateCanvas.emit();
  }

  addText() {
    const text = new fabric.Textbox('Escribe un texto...', {
      width: 200,
      height: 100,
      fontSize: 24,
      // fontFamily: 'Satoshi-Regular',
      cursorColor: '#FF4D80',
      left: 50,
      top: 50,
      eventSeat: false
    });
    this.canvas.add(text);
  }

  createTextComponent(text: any, fontsize: number, top: number = 0) {
    let textComponent = new fabric.Text(text, {
      // fontFamily: 'Satoshi-Regular',
      fontSize: fontsize,
      fill: 'black',
      textAlign: 'center',
      originX: 'center',
      originY: 'center',
      eventSeat: false,
      top: top
    });
    return textComponent;
  }

}


function getObjectSizeWithStroke(object) {
  var stroke = new fabric.Point(
    object.strokeUniform ? 1 / object.scaleX : 1,
    object.strokeUniform ? 1 / object.scaleY : 1
  ).multiply(object.strokeWidth);
  return new fabric.Point(object.width + stroke.x, object.height + stroke.y);
}

function actionHandler(eventData, transform, x, y) {
  var polygon = transform.target,
    currentControl = polygon.controls[polygon.__corner],
    mouseLocalPosition = polygon.toLocalPoint(new fabric.Point(x, y), 'center', 'center'),
    polygonBaseSize = getObjectSizeWithStroke(polygon),
    size = polygon._getTransformedDimensions(0, 0),
    finalPointPosition = {
      x: mouseLocalPosition.x * polygonBaseSize.x / size.x + polygon.pathOffset.x,
      y: mouseLocalPosition.y * polygonBaseSize.y / size.y + polygon.pathOffset.y
    };
  polygon.points[currentControl.pointIndex] = finalPointPosition;
  return true;
}

function anchorWrapper(anchorIndex, fn) {
  return function (eventData, transform, x, y) {
    var fabricObject = transform.target,
      absolutePoint = fabric.util.transformPoint({
        x: (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x),
        y: (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y),
      }, fabricObject.calcTransformMatrix()),
      actionPerformed = fn(eventData, transform, x, y),
      newDim = fabricObject._setPositionDimensions({}),
      polygonBaseSize = getObjectSizeWithStroke(fabricObject),
      newX = (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x) / polygonBaseSize.x,
      newY = (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y) / polygonBaseSize.y;
    fabricObject.setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
    return actionPerformed;
  }
}

function polygonPositionHandler(dim, finalMatrix, fabricObject) {
  var x = (fabricObject.points[this.pointIndex].x - fabricObject.pathOffset.x),
    y = (fabricObject.points[this.pointIndex].y - fabricObject.pathOffset.y);
  return fabric.util.transformPoint(
    { x: x, y: y },
    fabric.util.multiplyTransformMatrices(
      fabricObject.canvas.viewportTransform,
      fabricObject.calcTransformMatrix()
    )
  );
}

// Posición del tirador "+" de una arista: normalmente el punto medio de la arista;
// mientras se arrastra (ya insertado el vértice nuevo), sigue a ese vértice para que
// el tirador acompañe al cursor.
function midpointPositionHandler(dim, finalMatrix, fabricObject) {
  const active = fabricObject._activeMidpointInsert;
  let lx: number, ly: number;
  if (active && active.control === this) {
    const p = fabricObject.points[active.newIndex];
    lx = p.x - fabricObject.pathOffset.x;
    ly = p.y - fabricObject.pathOffset.y;
  } else {
    const n = fabricObject.points.length;
    const a = fabricObject.points[this.edgeIndex];
    const b = fabricObject.points[(this.edgeIndex + 1) % n];
    lx = (a.x + b.x) / 2 - fabricObject.pathOffset.x;
    ly = (a.y + b.y) / 2 - fabricObject.pathOffset.y;
  }
  return fabric.util.transformPoint(
    { x: lx, y: ly },
    fabric.util.multiplyTransformMatrices(
      fabricObject.canvas.viewportTransform,
      fabricObject.calcTransformMatrix()
    )
  );
}

// Handler de arrastre del tirador "+": en la primera llamada inserta un vértice nuevo en
// la arista (índice edgeIndex+1) en la posición del cursor; en las siguientes lo mueve.
function addVertexActionHandler(eventData, transform, x, y) {
  const polygon = transform.target;
  const ctrl = polygon.controls[polygon.__corner];
  const mouseLocalPosition = polygon.toLocalPoint(new fabric.Point(x, y), 'center', 'center');
  const polygonBaseSize = getObjectSizeWithStroke(polygon);
  const size = polygon._getTransformedDimensions(0, 0);
  const finalPointPosition = {
    x: mouseLocalPosition.x * polygonBaseSize.x / size.x + polygon.pathOffset.x,
    y: mouseLocalPosition.y * polygonBaseSize.y / size.y + polygon.pathOffset.y
  };
  if (!transform._vertexInserted) {
    const newIndex = ctrl.edgeIndex + 1;
    polygon.points.splice(newIndex, 0, finalPointPosition);
    // Mantener edgeCurvatures alineado: la arista edgeIndex se parte en dos; se inserta una
    // entrada nueva y se resetean ambos tramos (curvar sobre un vértice recién insertado
    // daría una geometría confusa).
    if (Array.isArray(polygon.edgeCurvatures)) {
      polygon.edgeCurvatures.splice(newIndex, 0, 0);
      polygon.edgeCurvatures[ctrl.edgeIndex] = 0;
    }
    transform._vertexInserted = true;
    transform._newVertexIndex = newIndex;
    polygon._activeMidpointInsert = { control: ctrl, newIndex };
  } else {
    polygon.points[transform._newVertexIndex] = finalPointPosition;
  }
  return true;
}

// Al soltar el arrastre de un "+": reconstruye los controles para normalizar índices y
// que aparezcan los nuevos tiradores de arista, y persiste el cambio.
function midpointMouseUpHandler(eventData, transform) {
  const polygon = transform?.target;
  if (polygon && polygon._activeMidpointInsert) {
    polygon._activeMidpointInsert = null;
    const ref = polygon._editComponentRef;
    if (ref) {
      ref.buildVertexControls();
      ref.updateCanvas.emit();
    }
    polygon.canvas && polygon.canvas.renderAll();
  }
  return true;
}

// Posición del tirador de curvatura de una arista: el ápice actual de la curva, es decir,
// el punto medio de la arista desplazado perpendicularmente edgeCurvatures[i]/2
// (el punto de control de la Bézier está al doble de distancia que el ápice).
function edgeCurvaturePositionHandler(dim, finalMatrix, fabricObject) {
  const n = fabricObject.points.length;
  const i = this.edgeIndex;
  const a = fabricObject.points[i];
  const b = fabricObject.points[(i + 1) % n];
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const px = -dy / len, py = dx / len;
  const c = (fabricObject.edgeCurvatures && fabricObject.edgeCurvatures[i]) || 0;
  const lx = mx + px * (c / 2) - fabricObject.pathOffset.x;
  const ly = my + py * (c / 2) - fabricObject.pathOffset.y;
  return fabric.util.transformPoint(
    { x: lx, y: ly },
    fabric.util.multiplyTransformMatrices(
      fabricObject.canvas.viewportTransform,
      fabricObject.calcTransformMatrix()
    )
  );
}

// Handler de arrastre del tirador de curvatura: proyecta la posición del cursor sobre la
// perpendicular a la arista y fija edgeCurvatures[i] = 2 * esa distancia (para que el ápice
// de la curva quede justo bajo el cursor). No mueve vértices.
function edgeCurvatureActionHandler(eventData, transform, x, y) {
  const polygon = transform.target;
  const ctrl = polygon.controls[polygon.__corner];
  const i = ctrl.edgeIndex;
  const n = polygon.points.length;
  const a = polygon.points[i];
  const b = polygon.points[(i + 1) % n];
  const mouseLocalPosition = polygon.toLocalPoint(new fabric.Point(x, y), 'center', 'center');
  const polygonBaseSize = getObjectSizeWithStroke(polygon);
  const size = polygon._getTransformedDimensions(0, 0);
  const localX = mouseLocalPosition.x * polygonBaseSize.x / size.x + polygon.pathOffset.x;
  const localY = mouseLocalPosition.y * polygonBaseSize.y / size.y + polygon.pathOffset.y;
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const px = -dy / len, py = dx / len;
  const apex = (localX - mx) * px + (localY - my) * py;
  if (!Array.isArray(polygon.edgeCurvatures)) {
    polygon.edgeCurvatures = new Array(n).fill(0);
  }
  polygon.edgeCurvatures[i] = apex * 2;
  polygon.dirty = true;
  return true;
}

// Al soltar el tirador de curvatura: persistir el cambio a través del componente.
function edgeCurvatureMouseUpHandler(eventData, transform) {
  const polygon = transform?.target;
  const ref = polygon?._editComponentRef;
  if (ref) { ref.updateCanvas.emit(); }
  polygon?.canvas && polygon.canvas.renderAll();
  return true;
}

// Dibuja el tirador de curvatura (círculo azul con doble raya, distinto del "+" rosa).
function renderCurveEdgeControl(ctx, left, top, styleOverride, fabricObject) {
  const r = 6;
  ctx.save();
  ctx.beginPath();
  ctx.arc(left, top, r, 0, 2 * Math.PI, false);
  ctx.fillStyle = 'rgba(45,140,255,0.5)';
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(45,140,255,0.95)';
  ctx.stroke();
  // Pequeña "onda" blanca para sugerir curvatura.
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(left - 3.5, top + 1.5);
  ctx.quadraticCurveTo(left, top - 3, left + 3.5, top + 1.5);
  ctx.stroke();
  ctx.restore();
}

// Dibuja el tirador "+" semitransparente (rosa Citylok) en una arista.
function renderAddVertexControl(ctx, left, top, styleOverride, fabricObject) {
  const r = 5.5;
  ctx.save();
  ctx.beginPath();
  ctx.arc(left, top, r, 0, 2 * Math.PI, false);
  ctx.fillStyle = 'rgba(255,77,128,0.45)';
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(255,77,128,0.9)';
  ctx.stroke();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(left - 3, top);
  ctx.lineTo(left + 3, top);
  ctx.moveTo(left, top - 3);
  ctx.lineTo(left, top + 3);
  ctx.stroke();
  ctx.restore();
}